import {
  createMatch,
  startGame,
  recordRally,
  undoLastRally,
  setServingSide,
  canUndoRally,
  resumeFromInterval,
  suggestCourtSetupAfterGame,
  getCurrentServer,
  getCurrentReceiver,
  getServerBox,
  DEFAULT_POINTS_TO_WIN,
  capForPointsToWin,
  serviceCourtForServerScore,
} from "./matchScoring";

/** UI `setsToWin` (games needed to win) → match `bestOf` (max games). */
export function setsToWinToBestOf(setsToWin) {
  const n = Number(setsToWin);
  if (!Number.isFinite(n) || n <= 1) return 1;
  return 2 * Math.floor(n) - 1;
}

export function playersFromTeam(team) {
  const slots = team?.slots;
  if (Array.isArray(slots) && slots.length) {
    return slots.map((s, i) => ({
      id: s.registryEntryId || `${team.name || "team"}-${i}`,
      name: s.displayName,
      registryEntryId: s.registryEntryId,
      playerId: s.playerId,
    }));
  }
  return (team.players || []).map((name, i) => ({
    id: `${team.name || "team"}-${i}-${typeof name === "string" ? name : name?.displayName || ""}`,
    name: typeof name === "string" ? name : name?.displayName || "",
  }));
}

export function initScoringMatch(
  teamA,
  teamB,
  setsToWin,
  endsSwapped,
  firstServerSide = "A",
  pointsToWin = DEFAULT_POINTS_TO_WIN
) {
  const bestOf = setsToWinToBestOf(setsToWin);
  const mode = teamA.type === "doubles" ? "doubles" : "singles";
  const sideAPlayers = playersFromTeam(teamA);
  const sideBPlayers = playersFromTeam(teamB);
  const serverPlayers = firstServerSide === "A" ? sideAPlayers : sideBPlayers;
  const firstServerId = serverPlayers[0]?.id;
  if (!firstServerId) throw new Error("initScoringMatch: missing players");

  const ptw = Math.max(
    1,
    Math.min(100, Math.trunc(Number(pointsToWin)) || DEFAULT_POINTS_TO_WIN)
  );
  const gameOpts = { pointsToWin: ptw, cap: capForPointsToWin(ptw) };

  let match = createMatch(bestOf);
  match = { ...match, endsSwapped: !!endsSwapped };
  return startGame(match, mode, sideAPlayers, sideBPlayers, firstServerSide, firstServerId, gameOpts);
}

/**
 * Start the next game in a multi-game match after court setup (preserves completed games).
 */
export function startNextGameFromCourtSetup(
  existingMatch,
  teamA,
  teamB,
  endsSwapped,
  firstServerSide,
  pointsToWin = DEFAULT_POINTS_TO_WIN
) {
  if (!existingMatch?.games?.length) {
    throw new Error("startNextGameFromCourtSetup: no completed games");
  }
  const lastGame = existingMatch.games[existingMatch.games.length - 1];
  const mode = teamA.type === "doubles" ? "doubles" : "singles";
  const sideAPlayers = playersFromTeam(teamA);
  const sideBPlayers = playersFromTeam(teamB);
  const suggestion = suggestCourtSetupAfterGame({
    ...existingMatch,
    endsSwapped: !!endsSwapped,
  });
  const serverPlayers =
    firstServerSide === "A" ? sideAPlayers : sideBPlayers;
  let firstServerId = serverPlayers[0]?.id;
  if (
    firstServerSide === suggestion.suggestedServerSide &&
    suggestion.suggestedServerPlayerId
  ) {
    const found = serverPlayers.find(
      (p) => p.id === suggestion.suggestedServerPlayerId
    );
    if (found) firstServerId = found.id;
  }
  if (!firstServerId) throw new Error("startNextGameFromCourtSetup: missing server");

  const ptw = Math.max(
    1,
    Math.min(100, Math.trunc(Number(pointsToWin)) || DEFAULT_POINTS_TO_WIN)
  );
  const gameOpts = {
    pointsToWin: ptw,
    winBy: lastGame.winBy,
    cap: capForPointsToWin(ptw),
  };

  const { awaitingNextGame: _drop, ...rest } = existingMatch;
  let base = { ...rest, endsSwapped: !!endsSwapped };
  return startGame(
    base,
    mode,
    sideAPlayers,
    sideBPlayers,
    firstServerSide,
    firstServerId,
    gameOpts
  );
}

export function matchToLiveView(match) {
  if (!match) {
    return {
      scoreA: 0,
      scoreB: 0,
      serving: "A",
      sets: [],
      winner: null,
      endsSwapped: false,
    };
  }
  const sets = match.games.map((g) => ({
    scoreA: g.sideA.score,
    scoreB: g.sideB.score,
    winner: g.gameWinner,
  }));
  const g = match.activeGame;
  const br = match.intervalBreak;
  let scoreA = g?.sideA.score ?? 0;
  let scoreB = g?.sideB.score ?? 0;
  if (!g && br) {
    scoreA = br.scoreA;
    scoreB = br.scoreB;
  }
  return {
    scoreA,
    scoreB,
    serving: g?.servingSide ?? "A",
    sets,
    winner: match.matchWinner,
    endsSwapped: match.endsSwapped,
  };
}

export function serializeScoringMatch(match) {
  return match ? JSON.stringify(match) : null;
}

export function deserializeScoringMatch(json) {
  if (!json) return null;
  try {
    return typeof json === "string" ? JSON.parse(json) : json;
  } catch {
    return null;
  }
}

/** Court UI shows players[0] in the right service box; align team order with scoring state. */
export function reorderTeamForServiceBoxes(team, sideState) {
  if (!team || !sideState || sideState.players.length < 2) return team;
  const right = sideState.players.find((p) => p.id === sideState.rightBoxPlayerId);
  const left = sideState.players.find((p) => p.id !== sideState.rightBoxPlayerId);
  if (!right || !left) return team;
  if (team.slots?.length >= 2) {
    const byId = new Map(team.slots.map((s) => [s.registryEntryId, s]));
    const ordered = [byId.get(right.id) || byId.get(right.registryEntryId), byId.get(left.id) || byId.get(left.registryEntryId)].filter(Boolean);
    if (ordered.length === 2) return { ...team, slots: ordered };
  }
  return team;
}

export function syncTeamsToActiveGameBoxes(teamA, teamB, match) {
  const g = match?.activeGame;
  if (!g || g.mode !== "doubles") return { teamA, teamB };
  return {
    teamA: reorderTeamForServiceBoxes(teamA, g.sideA),
    teamB: reorderTeamForServiceBoxes(teamB, g.sideB),
  };
}

export {
  recordRally,
  undoLastRally,
  setServingSide,
  canUndoRally,
  resumeFromInterval,
  suggestCourtSetupAfterGame,
  getCurrentServer,
  getCurrentReceiver,
  getServerBox,
  serviceCourtForServerScore,
};
